import { useState } from 'react';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shigo-ui/ui';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { dashboardT } from '../../../../../lib/messages';
import type { createCampaignsClient } from '../../api';
import {
  type MadBuyCreativeAssociation,
  type MadBuyLineItemGeneralForm,
} from '../../lineItemsModel';
import { CreativeAssociationDialog } from './CreativeAssociationDialog';
import type { LineItemFormUpdate } from './LineItemEditorTypes';

type MadBuyCampaignsClient = ReturnType<typeof createCampaignsClient>;

export function LineItemCreativesFields({
  brandId,
  client,
  error,
  form,
  updateField,
}: {
  brandId: string;
  client: MadBuyCampaignsClient;
  error?: string | undefined;
  form: MadBuyLineItemGeneralForm;
  updateField: LineItemFormUpdate;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  function upsertAssociation(association: MadBuyCreativeAssociation) {
    if (editingIndex === null) {
      updateField('lineItemCreatives', [...form.lineItemCreatives, association]);
      return;
    }

    updateField(
      'lineItemCreatives',
      form.lineItemCreatives.map((item, index) => (index === editingIndex ? association : item)),
    );
  }

  return (
    <DashboardPageFormBody width="lg">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-foreground text-xl font-bold">
            {dashboardT('madBuy.lineItems.creatives')}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {dashboardT('madBuy.lineItems.creativesDescription')}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => {
            setEditingIndex(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {dashboardT('madBuy.lineItems.newAssociation')}
        </Button>
      </div>

      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}

      <div className="border-border overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{dashboardT('madBuy.common.name')}</TableHead>
              <TableHead>{dashboardT('madBuy.lineItems.weight')}</TableHead>
              <TableHead className="w-32 text-right">
                {dashboardT('madBuy.common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {form.lineItemCreatives.length ? (
              form.lineItemCreatives.map((association, index) => (
                <TableRow key={association.id ?? association.creative.id}>
                  <TableCell className="font-medium">{association.creative.name}</TableCell>
                  <TableCell>{association.weight}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={dashboardT('madBuy.lineItems.editNamed', {
                          name: association.creative.name,
                        })}
                        onClick={() => {
                          setEditingIndex(index);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={dashboardT('madBuy.lineItems.removeNamed', {
                          name: association.creative.name,
                        })}
                        onClick={() => {
                          updateField(
                            'lineItemCreatives',
                            form.lineItemCreatives.filter((_, itemIndex) => itemIndex !== index),
                          );
                        }}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground h-24 text-center">
                  {dashboardT('madBuy.lineItems.noCreativeAssociations')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {dialogOpen ? (
        <CreativeAssociationDialog
          adType={form.adType}
          alreadySelectedIds={form.lineItemCreatives
            .map((association, index) =>
              index === editingIndex ? undefined : association.creative.id,
            )
            .filter(isDefined)}
          association={editingIndex === null ? undefined : form.lineItemCreatives[editingIndex]}
          brandId={brandId}
          client={client}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSave={(association) => {
            upsertAssociation(association);
            setDialogOpen(false);
          }}
        />
      ) : null}
    </DashboardPageFormBody>
  );
}

function isDefined<TValue>(value: TValue | undefined): value is TValue {
  return value !== undefined;
}
